import React from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, MinusIcon, PlusIcon, ShieldCheckIcon } from 'lucide-react';
import { includedGear, gearColumns, media, GearItem, GearStatus } from '../data/product';
import { requestOffer } from '../utils/offerBus';
import { useTrackOnView } from '../hooks/useTrackOnView';
type ColumnKey = (typeof gearColumns)[number]['key'];

/**
 * What ships with each way to buy, side by side: the $29.95 Essential only
 * includes what's listed for it; everything else is an add-on or comes with
 * the Complete System and multi-packs.
 */
function goToColumn(key: ColumnKey, event: React.MouseEvent<HTMLAnchorElement>) {
  if (key === 'essential') return; // plain #build-your-x1 anchor
  event.preventDefault();
  requestOffer(key === 'complete' ? 'single' : 'trio');
}
function StatusCell({
  status,
  column
}: {
  status: GearStatus;
  column: string;
}) {
  const Icon = status.kind === 'included' ? CheckIcon : status.kind === 'addon' ? PlusIcon : MinusIcon;
  const tone = status.kind === 'included' ? 'text-offwhite' : status.kind === 'addon' ? 'text-grit-400' : 'text-steel-500';
  const iconTone = status.kind === 'included' ? 'text-grit-500' : status.kind === 'addon' ? 'text-grit-400' : 'text-steel-600';
  return <div className="bg-ink-950 px-3 py-2.5 lg:bg-transparent lg:px-0 lg:py-0" data-id="element-634" data-mp="1ubg3l5hfinlm">
      <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-steel-500 lg:hidden" data-id="element-635" data-mp="1u9d1hn30540j">{column}</p>
      <p className={`mt-1 flex items-start gap-1.5 text-[13px] font-semibold leading-snug lg:mt-0 ${tone}`} data-id="element-636" data-mp="240zo0fksipil">
        <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconTone}`} aria-hidden="true" data-id="element-637" data-mp="2i0igk014bs9s" />
        {status.label}
      </p>
    </div>;
}
function GearThumb({
  item
}: {
  item: GearItem;
}) {
  if (item.image) {
    return <div className="aspect-[4/3] w-full overflow-hidden border border-white/10 bg-ink-900" data-id="element-638" data-mp="nly2w7b7swzw">
        <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" data-id="element-639" data-mp="iog2547cpo2z" />
      </div>;
  }
  return <div className="flex aspect-[4/3] w-full items-center justify-center border border-[#f2c94c]/40 bg-[#1a1408]" aria-hidden="true" data-id="element-640" data-mp="2irgwt7mci3xf">
      <ShieldCheckIcon className="h-10 w-10 text-[#f2c94c]" data-id="element-641" data-mp="2sobtrm2yzw9l" />
    </div>;
}
export function IncludedGear() {
  const ref = useTrackOnView<HTMLElement>('mystery_gift_offer_viewed');
  return <section ref={ref} aria-labelledby="included-gear-heading" className="mt-10 border border-white/12 bg-ink-950 lg:mt-12" data-id="element-642" data-mp="31p1mui0uj8jm">
      <div className="grid gap-6 border-b border-white/10 px-5 py-6 sm:px-7 lg:grid-cols-[1fr_380px] lg:items-center" data-id="element-643" data-mp="36n38napnplys">
        <div data-id="element-644" data-mp="3bfwczxh7qsac">
          <h3 id="included-gear-heading" className="grit-headline text-[clamp(1.5rem,4vw,2.5rem)] font-extrabold uppercase text-offwhite" data-id="element-645" data-mp="3bfd1ozm2j25a">
            What&apos;s in the box? <span className="text-grit-500" data-id="element-646" data-mp="3kh2sc384lx20">You choose.</span>
          </h3>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-steel-400" data-id="element-647" data-mp="3pf4bsp9grye0">
            The X1 Essential ships with the X1, one lens of your choice, a USB-C cable and the Quick Start Guide, plus
            only the add-ons you pick. The Complete System comes with everything below. Every X1 in a multi-pack is a
            Complete System.
          </p>
        </div>
        <figure className="border border-white/10 bg-ink-900" data-id="element-648" data-mp="zrztg1ch428b">
          <img src={media.completeInCase} alt="The X1 Complete System in its hard case: X1 glasses, Clear, Amber and Smoke lenses, charging cable and cleaning cloth" className="aspect-[4/3] w-full object-cover" loading="lazy" data-id="element-649" data-mp="ztkpjvefrt8a" />
          <figcaption className="border-t border-white/10 px-3 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-steel-400" data-id="element-650" data-mp="1nn2jgcn0kbqd">
            The Complete System, organized in its hard case
          </figcaption>
        </figure>
      </div>

      {/* Column headers (desktop) */}
      <div className="hidden grid-cols-[160px_minmax(0,1fr)_repeat(3,170px)] items-end gap-5 border-b border-white/10 px-7 py-4 lg:grid" data-id="element-651" data-mp="2bd6is7t65pbk">
        <span data-id="element-652" data-mp="1zybm87kdeowp" />
        <span data-id="element-653" data-mp="1zyux54blsd18" />
        {gearColumns.map(column => <a key={column.key} href={column.key === 'essential' ? '#build-your-x1' : '#offer'} onClick={event => goToColumn(column.key, event)} className="group block focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-654" data-mp="1zw8o1092dpgw">
            <span className="block font-display text-lg font-extrabold uppercase leading-none text-offwhite transition-colors duration-150 ease-grit group-hover:text-grit-500" data-id="element-655" data-mp="2epc7xea4ofas">
              {column.title}
            </span>
            <span className="mt-1 block text-[12px] font-bold uppercase tracking-[0.14em] text-grit-500" data-id="element-656" data-mp="29wj0ujr6suvj">
              {column.price}
            </span>
          </a>)}
      </div>

      <ul className="divide-y divide-white/10" data-id="element-657" data-mp="2xk94q4yr2u8n">
        {includedGear.map(item => <li key={item.name} className="grid gap-4 px-5 py-5 sm:grid-cols-[140px_minmax(0,1fr)] sm:px-7 lg:grid-cols-[160px_minmax(0,1fr)_repeat(3,170px)] lg:items-center lg:gap-5" data-id="element-658" data-mp="32ewpptujp6ih">
            <GearThumb item={item} data-id="element-659" data-mp="3lhfcfjefwahh" />
            <div data-id="element-660" data-mp="3lgds0objabn3">
              <p className="font-display text-xl font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-661" data-mp="3lgxcocskje4t">
                {item.name}
              </p>
              <p className="mt-1 text-[14px] leading-relaxed text-steel-300" data-id="element-662" data-mp="2ss86hzcnoim7">{item.note}</p>
              {item.termsLink && <Link to="/legal/guarantee" className="mt-1.5 inline-block text-[12px] text-steel-400 underline underline-offset-2 hover:text-offwhite" data-id="element-663" data-mp="2oshho16owckb">
                  See warranty &amp; protection terms
                </Link>}
            </div>
            <div className="grid grid-cols-3 gap-px bg-white/10 sm:col-span-2 lg:contents" data-id="element-664" data-mp="hyvkyj3l58nq">
              {gearColumns.map(column => <StatusCell key={column.key} status={item[column.key]} column={column.title} data-id="element-665" data-mp="l2l7n654e644" />)}
            </div>
          </li>)}
      </ul>

      {/* Column shortcuts (mobile / tablet) */}
      <div className="grid grid-cols-3 gap-px border-t border-white/10 bg-white/10 lg:hidden" data-id="element-666" data-mp="u2ht6yw7qouy">
        {gearColumns.map(column => <a key={column.key} href={column.key === 'essential' ? '#build-your-x1' : '#offer'} onClick={event => goToColumn(column.key, event)} className="bg-ink-900 px-3 py-3 transition-colors duration-150 ease-grit hover:bg-ink-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500" data-id="element-667" data-mp="z1kmjm2tz6n3">
            <span className="block font-display text-[15px] font-extrabold uppercase leading-tight text-offwhite" data-id="element-668" data-mp="g3i27kl89xvm">
              {column.title}
            </span>
            <span className="mt-0.5 block text-[11px] font-bold uppercase tracking-[0.12em] text-grit-500" data-id="element-669" data-mp="3ere4skx623a9">
              {column.price}
            </span>
          </a>)}
      </div>
    </section>;
}